/**
 * The POS's redux-reading wrapper around the shared pipeline — the fat-card twin of
 * `./useCatalogItems`.
 *
 * Both hooks read the same store (`./useCatalogContext`) and call the same `resolveItems`; they
 * differ only in which `CardBuilder` they pass, which is the whole point of `../posCard`. Two
 * named hooks rather than one with a flag, because their RETURN TYPES differ —
 * `PosCatalogItem[]` here, `CatalogItem[]` there — and a result type that flips on a boolean
 * argument is worse for callers than two names.
 *
 * 🔴 **NEW CODE WANTS `./useCatalogItems`.** This one exists to keep AS-IS POS surfaces
 * (`ItemsView`, `BarCodeSearch`, web's own favourites list, mobile's list card) rendering the
 * shape they were written against — `mid`, `isInActive`, `measuringUnit` and the raw item
 * itself. It retires with `posCard`, and every consumer moved off it is progress.
 *
 * Uplifted from `apps/web/src/hooks/commonHooks.ts`, cross-platform: every slice it reads exists
 * under the same key on both apps' stores.
 */
import { useMemo } from "react";

import { getCatalogItemsFromIds } from "../posCard";
import { useCatalogContext } from "./useCatalogContext";
import type { CatalogItemsFilterConfig } from "../posCard";

/**
 * Display-ready POS cards for `itemIds`, filtered per `filterConfig`.
 *
 * ⚠️ The memo keys off the individual filter FLAGS, not the config object's identity. The web
 * original listed `filterConfig` in its dependency array while every call site passes an inline
 * object literal, so the memo was rebuilt on every render and the projection re-ran each time.
 * Same output, less work. (`./useCatalogItems` makes the same correction for the lean card.)
 *
 * Pass a STABLE `itemIds` array (a `useMemo`'d one) — an inline literal re-runs the projection
 * every render.
 *
 * ⚠️ **Returns `any[]`, deliberately, and it is the ONE place in this domain that does.**
 * `getCatalogItemsFromIds` beneath it returns a typed `PosCatalogItem[]`; this hook widens it
 * again because every caller is AS-IS release code that annotates the result as its own local
 * `ItemObj` — a type declaring a REQUIRED `itemType` that `toPosCatalogItem` has never produced.
 * The declaration was always wrong and `any[]` was hiding it; narrowing here would turn a frozen
 * file's mistake into four build errors without making anything safer, because no new code may
 * use this hook. New code uses `./useCatalogItems`, where the types are real all the way through.
 */
export function useCatalogItemsFromIds(
  itemIds: string[],
  filterConfig: CatalogItemsFilterConfig = {},
): any[] {
  const context = useCatalogContext();
  const {
    resolveVariantToParent,
    skipVariants,
    scheduleAvailability,
    validPrice,
    channelEligibility,
    excludeSoldOuts,
  } = filterConfig;

  return useMemo(
    () =>
      getCatalogItemsFromIds(itemIds, context, {
        resolveVariantToParent,
        skipVariants,
        scheduleAvailability,
        validPrice,
        channelEligibility,
        excludeSoldOuts,
      }),
    [
      itemIds,
      context,
      resolveVariantToParent,
      skipVariants,
      scheduleAvailability,
      validPrice,
      channelEligibility,
      excludeSoldOuts,
    ],
  );
}
